import * as THREE from 'three';
import {
  PLAYER_RADIUS, PLAYER_SPEED, MAX_HP,
  ARENA_RADIUS, ROCK_COOLDOWN, SCISSORS_COOLDOWN, PAPER_COOLDOWN,
  SCISSORS_RANGE, SCISSORS_DAMAGE, SCISSORS_VS_PAPER_MULTIPLIER,
  INVULN_TIME, KNOCKBACK_FORCE,
  P1_COLOR, P2_COLOR,
  PLAYER_ACCEL, PLAYER_FRICTION,
  PLAYER_DASH_SPEED, PLAYER_DASH_DUR, PLAYER_DASH_COOLDOWN
} from './constants.js';

/* ============================================================
   Enhanced Player with improved model, combat, & state.
   Paper = held-up protective sheet (not called "shield").
   ============================================================ */

export class Player {
  constructor(id, scene) {
    this.id = id;
    this.scene = scene;
    this.color = id === 0 ? P1_COLOR : P2_COLOR;

    // ── State ──────────────────────────────
    this.hp = MAX_HP;
    this.alive = true;
    this.wins = 0;
    this.lastDamageSource = null;   // 'rock' | 'scissors'
    this.combatLog = [];            // recent events for UI

    // ── Movement ───────────────────────────
    this.position = new THREE.Vector3();
    this.velocity = new THREE.Vector3();
    this.facing = new THREE.Vector3(0, 0, id === 0 ? 1 : -1);
    this.facingAngle = id === 0 ? 0 : Math.PI; // Y rotation target
    this.moveInput = new THREE.Vector2();

    // ── Weapon state ───────────────────────
    this.currentWeapon = null; // 'rock' | 'paper' | 'scissors' | null
    this.isBlocking = false;
    this.isAttacking = false;
    this.attackTimer = 0;

    // Cooldowns (seconds remaining)
    this.rockCooldown = 0;
    this.scissorsCooldown = 0;
    this.paperCooldown = 0;

    // ── Combat ─────────────────────────────
    this.invulnTimer = 0;
    this.knockbackVel = new THREE.Vector3();
    this.hitFlashTimer = 0;
    this.staggerTimer = 0;          // brief stun after big hit

    // ── Dash ───────────────────────────────
    this.dashTimer = 0;
    this.dashCooldown = 0;
    this.dashDir = new THREE.Vector3();
    this.isDashing = false;

    // ── Animation ──────────────────────────
    this.walkCycle = 0;
    this.attackAnim = 0;
    this.blockAnim = 0;
    this.tiltAngle = 0;             // body lean on strafe

    // ── Build meshes ───────────────────────
    this.group = new THREE.Group();
    this._buildModel();
    this._buildWeapons();
    scene.add(this.group);
    this.reset();
  }

  // ══════════════════════════════════════════
  //  MODEL (enhanced gladiator)
  // ══════════════════════════════════════════

  _buildModel() {
    const teamHex = this.id === 0 ? 0x4488FF : 0xFF4444;
    const teamDark = this.id === 0 ? 0x1a3366 : 0x661a1a;

    // ── Materials (minimal set) ──
    const bodyMat = new THREE.MeshStandardMaterial({ 
      color: 0x2a2a2a, roughness: 0.4, metalness: 0.6 
    });
    const accentMat = new THREE.MeshStandardMaterial({ 
      color: teamHex, roughness: 0.3, metalness: 0.4 
    });
    const glowMat = new THREE.MeshStandardMaterial({ 
      color: teamHex, emissive: teamHex, emissiveIntensity: 0.8, roughness: 0.2 
    });
    const darkMat = new THREE.MeshStandardMaterial({ 
      color: 0x1a1a1a, roughness: 0.5, metalness: 0.3 
    });
    const visorMat = new THREE.MeshStandardMaterial({ 
      color: teamHex, emissive: teamHex, emissiveIntensity: 1.2, 
      transparent: true, opacity: 0.9 
    });

    // ═══ BODY (smooth capsule torso) ═══
    const torsoGeo = new THREE.CapsuleGeometry(0.55, 1.0, 8, 16);
    const torso = new THREE.Mesh(torsoGeo, bodyMat);
    torso.position.y = 2.0;
    torso.scale.set(1.2, 1, 0.8);
    torso.castShadow = true;
    this.group.add(torso);
    this.torso = torso;

    // Chest accent stripe
    const chestStripe = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 0.9, 0.5),
      glowMat
    );
    chestStripe.position.set(0, 2.0, 0.42);
    this.group.add(chestStripe);

    // Core glow ring
    const coreRing = new THREE.Mesh(
      new THREE.TorusGeometry(0.35, 0.04, 8, 24),
      glowMat
    );
    coreRing.position.set(0, 2.0, 0.45);
    this.group.add(coreRing);

    // ═══ SHOULDERS (simple spheres) ═══
    for (const side of [-1, 1]) {
      const shoulder = new THREE.Mesh(
        new THREE.SphereGeometry(0.28, 12, 10),
        bodyMat
      );
      shoulder.position.set(side * 0.72, 2.65, 0);
      shoulder.castShadow = true;
      this.group.add(shoulder);

      // Shoulder glow accent
      const shoulderGlow = new THREE.Mesh(
        new THREE.RingGeometry(0.15, 0.22, 16),
        glowMat
      );
      shoulderGlow.position.set(side * 0.72, 2.65, side * 0.15);
      shoulderGlow.rotation.y = side * 0.5;
      this.group.add(shoulderGlow);
    }

    // ═══ HEAD (sleek helmet) ═══
    const helmet = new THREE.Mesh(
      new THREE.SphereGeometry(0.42, 16, 12),
      bodyMat
    );
    helmet.position.y = 3.15;
    helmet.scale.set(1, 1.1, 1);
    helmet.castShadow = true;
    this.group.add(helmet);
    this.head = helmet;

    // Visor (glowing slit)
    const visor = new THREE.Mesh(
      new THREE.BoxGeometry(0.5, 0.08, 0.15),
      visorMat
    );
    visor.position.set(0, 3.2, 0.35);
    this.group.add(visor);

    // Helmet crest (simple fin)
    const crest = new THREE.Mesh(
      new THREE.BoxGeometry(0.06, 0.35, 0.5),
      accentMat
    );
    crest.position.set(0, 3.45, -0.1);
    this.group.add(crest);

    // ═══ ARMS ═══
    this.leftArm = this._createArm(bodyMat, darkMat, glowMat, -1);
    this.rightArm = this._createArm(bodyMat, darkMat, glowMat, 1);

    // ═══ LEGS ═══
    this.leftLeg = this._createLeg(bodyMat, darkMat, glowMat, -1);
    this.rightLeg = this._createLeg(bodyMat, darkMat, glowMat, 1);

    // ═══ HIP ARMOR (simple plates) ═══
    for (const side of [-1, 1]) {
      const hipPlate = new THREE.Mesh(
        new THREE.BoxGeometry(0.35, 0.4, 0.15),
        bodyMat
      );
      hipPlate.position.set(side * 0.45, 1.3, 0);
      this.group.add(hipPlate);
    }

    // ═══ TEAM GLOW LIGHT ═══
    const teamLight = new THREE.PointLight(teamHex, 0.8, 6);
    teamLight.position.set(0, 2.0, 0.5);
    this.group.add(teamLight);

    // ═══ SHADOW ═══
    const shadow = new THREE.Mesh(
      new THREE.CircleGeometry(0.9, 16),
      new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.4 })
    );
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.y = 0.02;
    this.group.add(shadow);
  }

  _createArm(bodyMat, darkMat, glowMat, side) {
    const arm = new THREE.Group();

    // Upper arm (capsule)
    const upper = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.12, 0.4, 4, 8),
      bodyMat
    );
    upper.position.y = -0.35;
    upper.castShadow = true;
    arm.add(upper);

    // Forearm (capsule)
    const fore = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.1, 0.35, 4, 8),
      darkMat
    );
    fore.position.y = -0.85;
    fore.castShadow = true;
    arm.add(fore);

    // Hand (sphere)
    const hand = new THREE.Mesh(
      new THREE.SphereGeometry(0.1, 8, 6),
      darkMat
    );
    hand.position.y = -1.15;
    arm.add(hand);

    // Glow band on forearm
    const band = new THREE.Mesh(
      new THREE.TorusGeometry(0.11, 0.02, 6, 12),
      glowMat
    );
    band.position.y = -0.7;
    band.rotation.x = Math.PI / 2;
    arm.add(band);

    arm.position.set(side * 0.75, 2.55, 0);
    this.group.add(arm);
    return arm;
  }

  _createLeg(bodyMat, darkMat, glowMat, side) {
    const leg = new THREE.Group();

    // Thigh (capsule)
    const thigh = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.14, 0.45, 4, 8),
      bodyMat
    );
    thigh.position.y = -0.35;
    thigh.castShadow = true;
    leg.add(thigh);

    // Shin (capsule)
    const shin = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.11, 0.4, 4, 8),
      darkMat
    );
    shin.position.y = -0.95;
    shin.castShadow = true;
    leg.add(shin);

    // Knee cap
    const knee = new THREE.Mesh(
      new THREE.SphereGeometry(0.08, 6, 5),
      bodyMat
    );
    knee.position.set(0, -0.6, 0.08);
    leg.add(knee);

    // Foot (box)
    const foot = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 0.1, 0.28),
      darkMat
    );
    foot.position.set(0, -1.3, 0.05);
    leg.add(foot);

    // Ankle glow
    const ankleGlow = new THREE.Mesh(
      new THREE.TorusGeometry(0.09, 0.015, 6, 10),
      glowMat
    );
    ankleGlow.position.y = -1.2;
    ankleGlow.rotation.x = Math.PI / 2;
    leg.add(ankleGlow);

    leg.position.set(side * 0.28, 1.25, 0);
    this.group.add(leg);
    return leg;
  }

  // ══════════════════════════════════════════
  //  WEAPONS
  // ══════════════════════════════════════════

  _buildWeapons() {
    // ── Rock (irregular boulder in right hand) ──
    const rockGeo = new THREE.IcosahedronGeometry(0.4, 1);
    const pos = rockGeo.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const v = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
      v.multiplyScalar(0.85 + Math.random() * 0.3);
      pos.setXYZ(i, v.x, v.y, v.z);
    }
    rockGeo.computeVertexNormals();
    this.rockMesh = new THREE.Mesh(rockGeo, new THREE.MeshStandardMaterial({
      color: 0x666666, roughness: 0.9, metalness: 0.15,
    }));
    this.rockMesh.visible = false;
    this.rockMesh.castShadow = true;
    this.rightArm.add(this.rockMesh);
    this.rockMesh.position.set(0, -1.2, 0.2);

    // ── Paper (held-up flat sheet in left hand) ──
    const paperGeo = new THREE.BoxGeometry(1.2, 1.4, 0.04);
    this.paperMesh = new THREE.Mesh(paperGeo, new THREE.MeshStandardMaterial({
      color: 0xFFF8E7, roughness: 0.3, metalness: 0, transparent: true, opacity: 0.9,
      side: THREE.DoubleSide,
    }));
    // Border glow
    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(paperGeo),
      new THREE.LineBasicMaterial({ color: 0xFFD700, linewidth: 2 })
    );
    this.paperMesh.add(edges);
    this.paperMesh.visible = false;
    this.paperMesh.castShadow = true;
    this.leftArm.add(this.paperMesh);
    this.paperMesh.position.set(0, -0.6, 0.45);

    // Paper glow
    this.paperGlow = new THREE.PointLight(0xFFF8E7, 0, 4);
    this.paperGlow.position.set(0, -0.6, 0.45);
    this.leftArm.add(this.paperGlow);

    // ── Scissors (sleek dual blades) ──
    this.scissorsGroup = new THREE.Group();
    const bladeMat = new THREE.MeshStandardMaterial({
      color: 0xDDDDDD, roughness: 0.1, metalness: 0.9,
    });
    // Two blades
    for (const s of [-1, 1]) {
      const blade = new THREE.Mesh(new THREE.BoxGeometry(0.05, 1.0, 0.15), bladeMat);
      blade.position.set(s * 0.07, -0.55, 0.06);
      blade.rotation.z = s * 0.1;
      this.scissorsGroup.add(blade);
    }
    // Pivot
    const pivot = new THREE.Mesh(
      new THREE.CylinderGeometry(0.08, 0.08, 0.08, 8),
      new THREE.MeshStandardMaterial({ color: 0x444444, metalness: 0.7, roughness: 0.3 })
    );
    pivot.position.set(0, -0.02, 0.06);
    pivot.rotation.x = Math.PI / 2;
    this.scissorsGroup.add(pivot);
    // Handle
    const handle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.08, 0.35, 8),
      new THREE.MeshStandardMaterial({ color: 0x333333, roughness: 0.6 })
    );
    handle.position.set(0, 0.18, 0.06);
    this.scissorsGroup.add(handle);
    this.scissorsGroup.visible = false;
    this.rightArm.add(this.scissorsGroup);
  }

  // ══════════════════════════════════════════
  //  LIFECYCLE
  // ══════════════════════════════════════════

  reset() {
    this.hp = MAX_HP;
    this.alive = true;
    this.currentWeapon = null;
    this.isBlocking = false;
    this.isAttacking = false;
    this.attackTimer = 0;
    this.rockCooldown = 0;
    this.scissorsCooldown = 0;
    this.paperCooldown = 0;
    this.invulnTimer = 0;
    this.knockbackVel.set(0, 0, 0);
    this.hitFlashTimer = 0;
    this.staggerTimer = 0;
    this.dashTimer = 0;
    this.dashCooldown = 0;
    this.isDashing = false;
    this.dashDir.set(0, 0, 0);
    this.walkCycle = 0;
    this.attackAnim = 0;
    this.blockAnim = 0;
    this.tiltAngle = 0;
    this.moveInput.set(0, 0);
    this.combatLog = [];
    this.lastDamageSource = null;

    const spawnZ = this.id === 0 ? -ARENA_RADIUS * 0.45 : ARENA_RADIUS * 0.45;
    this.position.set(0, 0, spawnZ);
    this.facing.set(0, 0, this.id === 0 ? 1 : -1);
    this.facingAngle = this.id === 0 ? 0 : Math.PI;

    this._updateWeaponVisuals();
    this._syncTransform();
  }

  // ══════════════════════════════════════════
  //  UPDATE
  // ══════════════════════════════════════════

  update(dt, zoneRadius) {
    if (!this.alive) return;

    // Timers
    if (this.rockCooldown > 0) this.rockCooldown -= dt;
    if (this.scissorsCooldown > 0) this.scissorsCooldown -= dt;
    if (this.paperCooldown > 0) this.paperCooldown -= dt;
    if (this.invulnTimer > 0) this.invulnTimer -= dt;
    if (this.hitFlashTimer > 0) this.hitFlashTimer -= dt;
    if (this.staggerTimer > 0) this.staggerTimer -= dt;
    if (this.attackTimer > 0) this.attackTimer -= dt;
    if (this.dashCooldown > 0) this.dashCooldown -= dt;

    // Stagger = can't move
    const canMove = this.staggerTimer <= 0 && !this.isDashing;

    // ── Dash ──
    if (this.isDashing) {
      this.dashTimer -= dt;
      if (this.dashTimer <= 0) {
        this.isDashing = false;
      } else {
        this.velocity.copy(this.dashDir).multiplyScalar(PLAYER_DASH_SPEED);
      }
    }

    // ── Acceleration-based movement ──
    if (!this.isDashing) {
      const moveDir = new THREE.Vector3(
        canMove ? this.moveInput.x : 0,
        0,
        canMove ? this.moveInput.y : 0
      );
      if (moveDir.lengthSq() > 0) {
        moveDir.normalize();
        this.facing.copy(moveDir);
      }

      const maxSpeed = this.isBlocking ? PLAYER_SPEED * 0.35 : PLAYER_SPEED;

      if (moveDir.lengthSq() > 0) {
        // Accelerate toward desired direction
        this.velocity.addScaledVector(moveDir, PLAYER_ACCEL * dt);
        // Clamp to max speed
        const hLen = Math.sqrt(this.velocity.x * this.velocity.x + this.velocity.z * this.velocity.z);
        if (hLen > maxSpeed) {
          const s = maxSpeed / hLen;
          this.velocity.x *= s;
          this.velocity.z *= s;
        }
      } else {
        // Friction: decelerate when no input
        const frictionMul = Math.max(0, 1 - PLAYER_FRICTION * dt);
        this.velocity.x *= frictionMul;
        this.velocity.z *= frictionMul;
      }
    }

    // Knockback
    if (this.knockbackVel.lengthSq() > 0.1) {
      this.velocity.add(this.knockbackVel);
      this.knockbackVel.multiplyScalar(Math.max(0, 1 - 5 * dt));
    } else {
      this.knockbackVel.set(0, 0, 0);
    }

    this.position.addScaledVector(this.velocity, dt);

    // ── FLOOR CLAMP — never go underground ──
    this.position.y = 0;

    // Arena bounds — always use full arena radius (zone only does damage, doesn't block)
    const distXZ = Math.sqrt(this.position.x * this.position.x + this.position.z * this.position.z);
    if (distXZ > ARENA_RADIUS - PLAYER_RADIUS) {
      const scale = (ARENA_RADIUS - PLAYER_RADIUS) / distXZ;
      this.position.x *= scale;
      this.position.z *= scale;
      // Wall bump – kill lateral knockback
      this.knockbackVel.multiplyScalar(0.3);
      // Bounce velocity inward
      const nx = this.position.x / distXZ;
      const nz = this.position.z / distXZ;
      const dot = this.velocity.x * nx + this.velocity.z * nz;
      if (dot > 0) {
        this.velocity.x -= nx * dot * 1.5;
        this.velocity.z -= nz * dot * 1.5;
      }
    }

    // Walk anim
    const hSpeed = Math.sqrt(this.velocity.x * this.velocity.x + this.velocity.z * this.velocity.z);
    if (hSpeed > 1) {
      this.walkCycle += dt * 11 * (hSpeed / PLAYER_SPEED);
    } else {
      this.walkCycle *= 0.88;
    }
    if (this.attackAnim > 0) this.attackAnim -= dt * 4.5;

    // Tilt based on strafe velocity
    const targetTilt = -this.velocity.x * 0.015;
    this.tiltAngle += (targetTilt - this.tiltAngle) * Math.min(dt * 10, 1);

    this._animateModel(dt);
    this._syncTransform();
  }

  /** Start a dash in the current move direction */
  doDash() {
    if (this.dashCooldown > 0 || this.staggerTimer > 0 || this.isDashing) return false;
    const dir = new THREE.Vector3(this.moveInput.x, 0, this.moveInput.y);
    if (dir.lengthSq() < 0.01) dir.copy(this.facing);
    dir.normalize();
    this.dashDir.copy(dir);
    this.dashTimer = PLAYER_DASH_DUR;
    this.dashCooldown = PLAYER_DASH_COOLDOWN;
    this.isDashing = true;
    return true;
  }

  // ══════════════════════════════════════════
  //  ANIMATION
  // ══════════════════════════════════════════

  _animateModel() {
    const ws = Math.sin(this.walkCycle) * 0.45;

    // Legs
    this.leftLeg.rotation.x = ws;
    this.rightLeg.rotation.x = -ws;

    // Arms idle / walk
    if (this.attackAnim <= 0 && !this.isBlocking) {
      this.leftArm.rotation.x = -ws * 0.4;
      this.rightArm.rotation.x = ws * 0.4;
      this.leftArm.rotation.z = 0;
      this.rightArm.rotation.z = 0;
    }

    // Attack anim
    if (this.attackAnim > 0) {
      if (this.currentWeapon === 'rock') {
        this.rightArm.rotation.x = -Math.PI * 0.55 * this.attackAnim;
      } else if (this.currentWeapon === 'scissors') {
        this.rightArm.rotation.x = -Math.PI * 0.45;
        this.rightArm.rotation.z = Math.sin(this.attackAnim * Math.PI * 3) * 0.9;
      }
    } else {
      this.rightArm.rotation.z *= 0.85;
    }

    // Blocking (paper)
    if (this.isBlocking) {
      this.leftArm.rotation.x = -Math.PI * 0.5;
      this.leftArm.rotation.z = 0.35;
      this.paperGlow.intensity = 1.8 + Math.sin(Date.now() * 0.006) * 0.6;
    } else {
      this.paperGlow.intensity = 0;
    }

    // Hit flash
    if (this.hitFlashTimer > 0) {
      this.group.visible = Math.sin(this.hitFlashTimer * 35) > 0;
    } else {
      this.group.visible = true;
    }
  }

  _syncTransform() {
    this.group.position.copy(this.position);
    if (this.facing.lengthSq() > 0) {
      const target = Math.atan2(this.facing.x, this.facing.z);
      let diff = target - this.group.rotation.y;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      this.group.rotation.y += diff * 0.22;
      this.facingAngle = this.group.rotation.y;
    }
    // Body lean on movement
    this.group.rotation.z = this.tiltAngle;
  }

  _updateWeaponVisuals() {
    this.rockMesh.visible = this.currentWeapon === 'rock';
    this.paperMesh.visible = this.isBlocking;
    this.scissorsGroup.visible = this.currentWeapon === 'scissors';
  }

  // ══════════════════════════════════════════
  //  ACTIONS
  // ══════════════════════════════════════════

  canUseRock()     { return this.rockCooldown <= 0 && this.staggerTimer <= 0; }
  canUseScissors() { return this.scissorsCooldown <= 0 && this.staggerTimer <= 0; }
  canUsePaper()    { return this.paperCooldown <= 0 && this.staggerTimer <= 0; }

  /** Throw rock – returns projectile spawn data or null */
  useRock() {
    if (!this.canUseRock()) return null;
    this.isBlocking = false;
    this.currentWeapon = 'rock';
    this.rockCooldown = ROCK_COOLDOWN;
    this.attackAnim = 1.0;
    this._updateWeaponVisuals();
    return {
      origin: this.position.clone().add(new THREE.Vector3(0, 1.5, 0)),
      direction: this.facing.clone().normalize(),
      owner: this.id,
    };
  }

  /** Start paper block */
  startBlocking() {
    if (!this.canUsePaper()) return;
    this.currentWeapon = 'paper';
    this.isBlocking = true;
    this._updateWeaponVisuals();
  }

  /** Release paper block */
  stopBlocking() {
    this.isBlocking = false;
    this.paperCooldown = PAPER_COOLDOWN;
    this._updateWeaponVisuals();
  }

  /**
   * Melee scissors attack — returns hit info or false.
   * Does cone-shaped hit detection for more reliable melee.
   */
  useScissors(otherPlayer) {
    if (!this.canUseScissors()) return false;
    this.isBlocking = false;
    this.currentWeapon = 'scissors';
    this.scissorsCooldown = SCISSORS_COOLDOWN;
    this.attackAnim = 1.0;
    this._updateWeaponVisuals();

    // Distance check
    const dist = this.position.distanceTo(otherPlayer.position);
    if (dist > SCISSORS_RANGE) return false;

    // Cone check: must be facing opponent within ~100° arc
    const toOther = new THREE.Vector3()
      .subVectors(otherPlayer.position, this.position).normalize();
    const dot = this.facing.dot(toOther);
    if (dot < 0.15) return false;

    // Damage
    let damage = SCISSORS_DAMAGE;
    let crit = false;
    if (otherPlayer.isBlocking) {
      damage *= SCISSORS_VS_PAPER_MULTIPLIER;
      crit = true;
    }

    return { damage, target: otherPlayer, crit };
  }

  // ══════════════════════════════════════════
  //  TAKING DAMAGE
  // ══════════════════════════════════════════

  /**
   * Apply damage. Returns true if damage was applied.
   * @param {number} amount
   * @param {THREE.Vector3} fromDirection – direction damage came from
   * @param {string} source – 'rock' | 'scissors'
   */
  takeDamage(amount, fromDirection, source = 'unknown') {
    if (this.invulnTimer > 0) return false;

    this.hp = Math.max(0, this.hp - amount);
    this.invulnTimer = INVULN_TIME;
    this.hitFlashTimer = INVULN_TIME;
    this.lastDamageSource = source;

    // Knockback — horizontal only, never push underground
    if (fromDirection) {
      const kbForce = source === 'rock' ? KNOCKBACK_FORCE * 1.2 : KNOCKBACK_FORCE;
      this.knockbackVel.copy(fromDirection).normalize().multiplyScalar(kbForce);
      this.knockbackVel.y = 0; // NEVER push player vertically
    }

    // Brief stagger on heavy hits
    if (amount >= 30) {
      this.staggerTimer = 0.25;
    }

    // If scissors destroyed paper block
    if (source === 'scissors' && this.isBlocking) {
      this.stopBlocking();
      this.staggerTimer = 0.4;
    }

    this.combatLog.push({ time: Date.now(), source, damage: amount });
    if (this.combatLog.length > 20) this.combatLog.shift();

    if (this.hp <= 0) {
      this.alive = false;
    }
    return true;
  }

  // ══════════════════════════════════════════
  //  SERIALIZATION (network)
  // ══════════════════════════════════════════

  getState() {
    // Compact — round floats to 2 decimals to reduce JSON size
    const r = (v) => Math.round(v * 100) / 100;
    return {
      id: this.id,
      p: [r(this.position.x), r(this.position.z)],
      f: [r(this.facing.x), r(this.facing.z)],
      hp: this.hp,
      a: this.alive ? 1 : 0,
      w: this.currentWeapon,
      b: this.isBlocking ? 1 : 0,
      m: [r(this.moveInput.x), r(this.moveInput.y)],
    };
  }

  applyState(state) {
    // Support both compact and legacy format
    if (state.p) {
      this.position.set(state.p[0], 0, state.p[1]);
      this.facing.set(state.f[0], 0, state.f[1]);
      this.hp = state.hp;
      this.alive = !!state.a;
      this.currentWeapon = state.w;
      this.isBlocking = !!state.b;
      this.moveInput.set(state.m[0], state.m[1]);
    } else {
      this.position.set(state.position.x, state.position.y, state.position.z);
      this.facing.set(state.facing.x, state.facing.y, state.facing.z);
      this.hp = state.hp;
      this.alive = state.alive;
      this.currentWeapon = state.currentWeapon;
      this.isBlocking = state.isBlocking;
      this.moveInput.set(state.moveInput.x, state.moveInput.y);
    }
    this._updateWeaponVisuals();
    this._syncTransform();
  }

  dispose() {
    this.scene.remove(this.group);
  }
}
